<template>
    <vx-table-row>
        <vx-table-row-title>
            <div class="flex items-center gap-3">
                <div class="cursor-pointer" data-test="toggle-favorite" @click="toggleFavorite()">
                    <font-awesome-icon
                        widthAuto
                        v-if="user.is_favorite"
                        :icon="faStar"
                        class="text-amber-400"
                    ></font-awesome-icon>
                    <font-awesome-icon widthAuto v-else :icon="faStarReg" class="text-slate-500"></font-awesome-icon>
                </div>
                <div>
                    {{ user.name || '-' }}
                </div>
            </div>
        </vx-table-row-title>

        <vx-table-row-item>
            <template v-slot:label> Email: </template>
            <vx-popover hover arrow placement="top" class="[&>div:first-child]:truncate w-full">
                {{ user.email || '-' }}
                <template v-if="user.email" v-slot:content>
                    {{ user.email || '-' }}
                </template>
            </vx-popover>
        </vx-table-row-item>

        <vx-table-row-item>
            <template v-slot:label> Current Team: </template>
            {{ user.current_team_name || '-' }}
        </vx-table-row-item>

        <vx-table-row-item data-test="status">
            <template v-slot:label> Status: </template>
            <vx-popover hover placement="top" arrow>
                <span>{{ user.suspended_at ? 'Suspended' : 'Active' }}</span>
                <template v-if="user.suspension_reason" v-slot:content>
                    <div class="max-w-xs">
                        {{ user.suspension_reason }}
                    </div>
                </template>
            </vx-popover>
        </vx-table-row-item>

        <vx-table-row-buttons>
            <vx-dropdown placement="right">
                <vx-button data-test="dropdown" size="sm" type="button" color="muted" outline class="w-9">
                    <font-awesome-icon widthAuto :icon="faEllipsis" />
                </vx-button>

                <template v-slot:items>
                    <vx-dropdown-item :href="`/kiosk/users/${user.id}`">
                        View
                        <font-awesome-icon widthAuto :icon="faEye"></font-awesome-icon>
                    </vx-dropdown-item>
                    <vx-dropdown-item v-if="user.is_favorite" @click="toggleFavorite()">
                        Remove Favorite
                        <font-awesome-icon widthAuto :icon="faStar"></font-awesome-icon>
                    </vx-dropdown-item>
                    <vx-dropdown-item v-else @click="toggleFavorite()">
                        Favorite
                        <font-awesome-icon widthAuto :icon="faStarReg"></font-awesome-icon>
                    </vx-dropdown-item>
                    <vx-dropdown-item v-if="user.suspended_at" @click="handleUnsuspend" data-test="unsuspend">
                        Unsuspend
                        <font-awesome-icon widthAuto :icon="faPlay"></font-awesome-icon>
                    </vx-dropdown-item>
                    <vx-dropdown-item v-else @click="handleSuspend" data-test="suspend">
                        Suspend
                        <font-awesome-icon widthAuto :icon="faPause"></font-awesome-icon>
                    </vx-dropdown-item>
                </template>
            </vx-dropdown>

            <vx-button
                :href="`/kiosk/users/${user.id}/impersonate`"
                :disabled="currentUserId === user.id || user.current_team_id === null || !!user.suspended_at"
                size="sm"
                data-test="impersonate"
                outline
                color="muted"
            >
                Impersonate
                <font-awesome-icon widthAuto :icon="faMasksTheater" />
            </vx-button>
        </vx-table-row-buttons>

        <kiosk-user-suspend-modal
            v-model:visible="suspendModal"
            v-model:user="user"
            :action="suspendModalAction"
        ></kiosk-user-suspend-modal>
    </vx-table-row>
</template>

<script setup>
import { faEllipsis, faEye, faStar, faMasksTheater, faPause, faPlay } from '@fortawesome/pro-solid-svg-icons';
import { faStar as faStarReg } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import {
    VxButton,
    VxDropdown,
    VxDropdownItem,
    VxTableRow,
    VxTableRowButtons,
    VxTableRowItem,
    VxTableRowTitle,
    VxPopover,
} from '@voxie/frontend-components';
import { useToasted } from '~/composables/useToasted';
import { adminUsersClient } from '~/services';
import { ref } from 'vue';
import { HTTP_INTERNAL_SERVER_ERROR } from '~/support/HttpStatuses';
import KioskUserSuspendModal from '../modals/KioskUserSuspendModal.vue';

const user = defineModel({
    required: true,
    type: Object,
});

const toasted = useToasted();
const togglingFavorite = ref(false);
const currentUserId = Spark.state.user.id;

const suspendModal = ref(false);
const suspendModalAction = ref('suspend');

const handleSuspend = () => {
    suspendModalAction.value = 'suspend';
    suspendModal.value = true;
};

const handleUnsuspend = () => {
    suspendModalAction.value = 'unsuspend';
    suspendModal.value = true;
};
const toggleFavorite = async () => {
    if (togglingFavorite.value) {
        return;
    }

    togglingFavorite.value = true;
    try {
        if (user.value.is_favorite) {
            await adminUsersClient.deleteFavoriteUser(user.value.id);
            user.value.is_favorite = false;
            toasted.success(`${user.value.id} - ${user.value.name} has been removed from your favorite users.`);
        } else {
            await adminUsersClient.favoriteUser(user.value.id);
            user.value.is_favorite = true;
            toasted.success(`${user.value.id} - ${user.value.name} has been added to your favorite users.`);
        }
    } catch (e) {
        console.error(e);
        if (e?.response?.status >= HTTP_INTERNAL_SERVER_ERROR) {
            toasted.global.platform_error();
            return;
        }

        toasted.error(e.response?.data?.message || 'Something went wrong.');
    } finally {
        togglingFavorite.value = false;
    }
};
</script>
